import React from 'react';

import styles from './RemainingRMD.module.scss';
import { useSelector } from 'react-redux';
import {
  getFirstRMDDeadline,
  getIsRMDPending,
  getLastBusinessDayOfYear,
  getRMDFactorAmount,
  getRMDSummary,
} from '../../../redux/modules/RMD/RMDSetting.selectors';
import { currency } from '../../../utils/dashboard-utils';
import Speedometer from './Speedometer/Speedometer';
import RMDCalculator from './RMDCalculator';
import {
  doesUserHaveAutomaticSellOrExchange,
  getOnlyOWNRRMDs,
} from '../../../redux/modules/UserFunds/userFunds.selectors';
import { currentYear, formatUnixDate, yearUserTurningRMDEligible } from '../../../utils/date-utils';

const RemainingRMD = () => {
  const rmdSummary = useSelector(getRMDSummary);
  const hasRMD = useSelector(getOnlyOWNRRMDs);
  const settledDistributions = rmdSummary.actualDistributionTotal;
  const hasAutoSellOrExchange = useSelector(
    doesUserHaveAutomaticSellOrExchange
  );
  const firstRMDDeadline = useSelector(getFirstRMDDeadline);
  const lastBusinessDayOfYear = useSelector(getLastBusinessDayOfYear);
  const totalRMDNoCurrency = rmdSummary.totalRMD;
  const isPending = useSelector(getIsRMDPending);
  const isAutoRMDPending = isPending.pending;
  const rmdLifeExpectancy = useSelector(getRMDFactorAmount);
  const multipleLE = rmdLifeExpectancy.length > 1 ? true : false;
  const year = new Date().getFullYear();
  const totalRMD = currency(rmdSummary.totalRMD);
  const amountDistToDate = currency(rmdSummary.amountToBeDistributed);

  const isPostLaunch = (dateAndTime) => {
    const currentDate = new Date();
    const launchDate = new Date(dateAndTime);
    return currentDate > launchDate;
  };
  return (
    <div className={styles['container']}>
      <div className={styles['container-left']}>
        <div className={styles['title']}>
          Your T. Rowe Price {year} Required Minimum Distribution (RMD)
        </div>
        {hasRMD.length === 0 &&
          settledDistributions === 0 &&
          !hasAutoSellOrExchange && (
            <div className={styles['description']}>
              Beginning the year you reach RMD age, you need to start taking money out
              of your applicable IRA(s) to avoid possible tax penalties.
              <div className={styles['description-pt2']}>
                You can set up an Auto-RMD plan, or take manual distributions.
              </div>
            </div>
          )}
        {hasRMD.length === 0 &&
          settledDistributions !== 0 &&
          settledDistributions < totalRMDNoCurrency &&
          (!hasAutoSellOrExchange || hasAutoSellOrExchange) && (
            <div className={styles['description']}>
              Continue to make progress on your RMD by withdrawing the required
              amount.
              <div className={styles['description-pt2']}>
                You can set up an Auto-RMD plan, or take manual distributions.
              </div>
            </div>
          )}
        {hasRMD.length === 0 &&
          settledDistributions === 0 &&
          hasAutoSellOrExchange && (
            <div className={styles['description']}>
              Continue to make progress on your RMD by withdrawing the required
              amount.
              <div className={styles['description-pt2']}>
                You can set up an Auto-RMD plan, or take manual distributions.
              </div>
            </div>
          )}
        {hasRMD.length === 0 &&
          settledDistributions >= totalRMDNoCurrency &&
          !hasAutoSellOrExchange && (
            <div className={styles['description']}>
              You’ve fulfilled your required minimum distribution for the year.
              <div className={styles['description-pt2']}>
                You can review your detailed history* below.
              </div>
            </div>
          )}
        {(hasRMD.length !== 0 || isAutoRMDPending) && (
          <div className={styles['description']}>
            You have Auto-RMD enabled.
            <div className={styles['description-pt2']}>
              Your RMD amount will be recalculated and automatically distributed
              each year.
            </div>
          </div>
        )}
        <div className={styles['deadline']}>
          {isPostLaunch('2024-01-01T00:01:00-05:00') ? (
            <div className="rmd-amount-deadline">
              {' '}
              Deadline for {year} RMD:{' '}
              {`${
                yearUserTurningRMDEligible() === currentYear('YYYY')
                  ? formatUnixDate(firstRMDDeadline)
                  : formatUnixDate(lastBusinessDayOfYear)
              } `}
            </div>
          ) : 
            'Deadline for ' + year + ' RMD: December 29, ' + year
          }
        </div>
        <div className={styles['amounts']}>
          <div>
            <div className={styles['rmd-titles']}>{year} RMD Amount</div>
            <div className={styles['contributed-amount']}>{totalRMD}</div>
            <RMDCalculator
              rmdSummary={rmdSummary}
              year={year}
              totalRMD={totalRMD}
              multipleLE={multipleLE}
              rmdLifeExpectancy={rmdLifeExpectancy}
            />{' '}
            {/* Multiple LE to be implemented later */}
          </div>
          <div className={styles['border']} />
          <div>
            <div className={styles['rmd-titles']}>Remaining RMD</div>
            <div className={styles['remaining-amount']}>{amountDistToDate}</div>
          </div>
        </div>
      </div>
      <Speedometer />
    </div>
  );
};

export default RemainingRMD;
